import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { PageContext, ResultCategory } from 'components/commandPalette/types';

/**
 * Determines search result ordering based on the current page.
 * This only affects the "general" search mode (no prefix).
 * Pinned cases are always boosted to the top separately.
 */
export function useSearchContext(): PageContext & { priorityOrder: ResultCategory[] } {
  const location = useLocation();

  return useMemo(() => {
    const pathname = location.pathname;

    let module: PageContext['module'] = 'other';
    let subpage: string | undefined;
    let priorityOrder: ResultCategory[];

    if (pathname.startsWith('/cantrack')) {
      module = 'cantrack';
      const sub = pathname.replace(/^\/cantrack\/?/, '').split('/')[0];
      subpage = sub || 'dashboard';

      if (subpage === 'timesheet' || subpage === 'comments') {
        // On timesheets/comments, prioritize cases (since entries are tied to cases)
        priorityOrder = ['cases', 'customers', 'contacts', 'navigation'];
      } else {
        priorityOrder = ['cases', 'contacts', 'customers', 'navigation'];
      }
    } else if (pathname.startsWith('/contacts')) {
      module = 'contacts';
      priorityOrder = ['contacts', 'cases', 'customers', 'navigation'];
    } else if (pathname.startsWith('/customers')) {
      module = 'customers';
      priorityOrder = ['customers', 'contacts', 'cases', 'navigation'];
    } else if (pathname === '/') {
      module = 'home';
      priorityOrder = ['cases', 'navigation', 'contacts', 'customers'];
    } else {
      priorityOrder = ['navigation', 'cases', 'contacts', 'customers'];
    }

    return { pathname, module, subpage, priorityOrder };
  }, [location.pathname]);
}
